import { BarChart3, Flag, TriangleAlert, Trophy, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BarChart } from '@/components/charts/bar-chart';
import { DonutChart } from '@/components/charts/donut-chart';
import { LineChart } from '@/components/charts/line-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatDateValue, toNumber } from '@/lib/utils';

import { MetasNav } from '../components/metas-nav';
import { useMetasReportes } from '../hooks/use-metas';
import { avance, ESTADO_TONES, estadoLabel, TONO_A_BADGE, tipoLabel } from '../lib/metas-format';

const COLUMNAS_RANKING = 'grid grid-cols-[minmax(0,1fr)_4rem_6rem_7rem_7rem] items-center gap-x-3';
const COLUMNAS_METAS =
  'grid grid-cols-[minmax(0,1fr)_6rem_6.5rem_5rem_6rem] items-center gap-x-3';

/** Indicador resumido del reporte. */
function Indicador({
  label,
  valor,
  detalle,
  tono = 'ink',
}: {
  label: string;
  valor: string;
  detalle?: string;
  tono?: 'brand' | 'emerald' | 'rose' | 'ink';
}) {
  const TONOS: Record<string, string> = {
    brand: 'border-brand-200 bg-brand-50/60',
    emerald: 'border-emerald-200 bg-emerald-50/60',
    rose: 'border-rose-200 bg-rose-50/60',
    ink: 'border-ink-100 bg-white',
  };

  return (
    <div className={cn('rounded-card border px-4 py-3', TONOS[tono])}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">{valor}</p>
      {detalle ? <p className="mt-0.5 text-xs text-ink-500">{detalle}</p> : null}
    </div>
  );
}

/**
 * Reportes de metas (`/metas/reportes`).
 *
 * Resumen general, distribución por estado y prioridad, desempeño por tipo,
 * evolución mensual y ranking de responsables.
 */
export function MetasReportesPage() {
  useDocumentTitle('Metas · Reportes');

  const { data, isPending, isError, refetch, isRefetching } = useMetasReportes();

  const resumen = data?.report_summary;
  const graficos = data?.report_charts;
  const ranking = data?.responsible_ranking ?? [];
  const metas = data?.goals ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Seguimiento</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <BarChart3 className="size-6 text-brand-500" aria-hidden="true" />
            Reportes de metas
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Cumplimiento consolidado de todas las metas registradas en el sistema.
          </p>
        </div>

        <MetasNav active="reportes" className="justify-end" />
      </header>

      {isError ? (
        <Alert variant="error" title="No se pudieron cargar los reportes">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {isPending || !resumen || !graficos ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-20 rounded-card" />
            ))}
          </div>
          <Skeleton className="h-72 rounded-card" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Indicador
              label="Metas registradas"
              valor={String(resumen.total_goals)}
              detalle={`${resumen.active} activas · ${resumen.completed} cumplidas`}
            />
            <Indicador
              label="Avance promedio"
              valor={`${toNumber(resumen.avg_progress).toFixed(2)} %`}
              detalle="Sobre el objetivo de cada meta"
              tono="brand"
            />
            <Indicador
              label="Tasa de cumplimiento"
              valor={`${toNumber(resumen.completion_rate).toFixed(2)} %`}
              detalle={`${resumen.completed} de ${resumen.total_goals} metas cumplidas`}
              tono="emerald"
            />
            <Indicador
              label="Metas en riesgo"
              valor={String(resumen.at_risk)}
              detalle={
                resumen.at_risk > 0
                  ? 'Vencen pronto y van bajo el 50 %'
                  : 'Ninguna meta en riesgo'
              }
              tono={resumen.at_risk > 0 ? 'rose' : 'ink'}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="brand">{resumen.active} activas</Badge>
            <Badge variant="success">{resumen.completed} cumplidas</Badge>
            <Badge variant="warning">{resumen.paused} en pausa</Badge>
            <Badge variant="neutral">{resumen.cancelled} canceladas</Badge>
          </div>

          {/* Distribuciones */}
          <div className="grid items-start gap-6 xl:grid-cols-2">
            <SectionCard title="Metas por estado" description="Distribución actual del catálogo">
              <DonutChart
                labels={graficos.status.labels}
                series={[{ label: 'Metas', data: graficos.status.values }]}
              />
            </SectionCard>

            <SectionCard title="Metas por prioridad" description="Cómo se reparte la exigencia">
              <DonutChart
                labels={graficos.priority.labels}
                series={[{ label: 'Metas', data: graficos.priority.values }]}
              />
            </SectionCard>

            <SectionCard
              title="Avance promedio por tipo"
              description="Cumplimiento medio de cada tipo de meta"
            >
              <BarChart
                labels={graficos.types.labels}
                format="percent"
                height={260}
                series={[{ label: 'Avance promedio', data: graficos.types.avg_progress }]}
              />
            </SectionCard>

            <SectionCard title="Metas por tipo" description="Cantidad de metas registradas por tipo">
              <BarChart
                labels={graficos.types.labels}
                height={260}
                series={[{ label: 'Metas', data: graficos.types.totals }]}
              />
            </SectionCard>

            <SectionCard
              className="xl:col-span-2"
              title="Evolución del avance"
              description="Avance promedio de las metas según su fecha de inicio"
            >
              <LineChart
                labels={graficos.trend.labels}
                format="percent"
                height={280}
                series={[{ label: 'Avance promedio', data: graficos.trend.avg_progress }]}
              />

              <ul className="mt-4 grid gap-2 border-t border-ink-100 pt-4 sm:grid-cols-3">
                {graficos.trend.labels.map((etiqueta, index) => (
                  <li
                    key={etiqueta}
                    className="flex items-center justify-between rounded-control bg-ink-50/70 px-3 py-2 text-xs"
                  >
                    <span className="font-medium text-ink-700">{etiqueta}</span>
                    <span className="font-mono text-ink-500 tabular-nums">
                      {graficos.trend.goal_counts[index] ?? 0}{' '}
                      {(graficos.trend.goal_counts[index] ?? 0) === 1 ? 'meta' : 'metas'}
                    </span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>

          {/* Ranking */}
          <SectionCard
            title="Ranking de responsables"
            description="Cumplimiento agrupado por responsable asignado"
            action={<Users className="size-4 text-ink-400" aria-hidden="true" />}
          >
            {ranking.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500">
                Sin datos suficientes para el ranking.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[42rem]">
                  <div
                    className={cn(
                      COLUMNAS_RANKING,
                      'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                    )}
                  >
                    <span>Responsable</span>
                    <span className="text-right">Metas</span>
                    <span className="text-right">Cumplidas</span>
                    <span className="text-right">Avance prom.</span>
                    <span className="text-right">Tasa</span>
                  </div>

                  <ul className="mt-1">
                    {ranking.map((fila) => (
                      <li
                        key={fila.name}
                        className={cn(COLUMNAS_RANKING, 'border-b border-ink-100 py-2 last:border-b-0')}
                      >
                        <span className="truncate text-sm font-medium text-ink-800">{fila.name}</span>
                        <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                          {fila.total}
                        </span>
                        <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                          {fila.completed}
                        </span>
                        <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
                          {toNumber(fila.avg_progress).toFixed(2)} %
                        </span>
                        <span className="flex justify-end">
                          <Badge
                            variant={
                              toNumber(fila.completion_rate) >= 100
                                ? 'success'
                                : toNumber(fila.completion_rate) >= 50
                                  ? 'warning'
                                  : 'neutral'
                            }
                          >
                            {toNumber(fila.completion_rate).toFixed(2)} %
                          </Badge>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </SectionCard>

          {/* Detalle de metas del reporte */}
          <SectionCard
            title="Metas incluidas"
            description="Todas las metas consideradas en el reporte"
            action={<Badge variant="outline">{metas.length}</Badge>}
          >
            {metas.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500">
                Todavía no hay metas registradas.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[46rem]">
                  <div
                    className={cn(
                      COLUMNAS_METAS,
                      'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                    )}
                  >
                    <span>Meta</span>
                    <span>Tipo</span>
                    <span>Estado</span>
                    <span className="text-right">Avance</span>
                    <span className="text-right">Período</span>
                  </div>

                  <ul className="mt-1">
                    {metas.map((goal) => (
                      <li
                        key={goal.id}
                        className={cn(COLUMNAS_METAS, 'border-b border-ink-100 py-2 last:border-b-0')}
                      >
                        <Link
                          to={`/metas/${goal.id}`}
                          className="truncate text-sm font-medium text-ink-800 transition-colors hover:text-brand-700"
                        >
                          {goal.goal_name}
                        </Link>
                        <span className="truncate text-xs text-ink-500">
                          {tipoLabel(goal.goal_type)}
                        </span>
                        <span>
                          <Badge variant={TONO_A_BADGE[ESTADO_TONES[goal.status]]}>
                            {estadoLabel(goal.status)}
                          </Badge>
                        </span>
                        <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
                          {avance(goal).toFixed(2)} %
                        </span>
                        <span className="text-right text-xs text-ink-500">
                          {formatDateValue(goal.start_date)} → {formatDateValue(goal.end_date)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-4 border-t border-ink-100 pt-4 text-xs text-ink-500">
              <span className="flex items-center gap-1.5">
                <Trophy className="size-3.5 text-emerald-500" aria-hidden="true" />
                {resumen.completed} metas cumplidas
              </span>
              <span className="flex items-center gap-1.5">
                <Flag className="size-3.5 text-brand-500" aria-hidden="true" />
                {resumen.active} metas activas
              </span>
              <span className="flex items-center gap-1.5">
                <TriangleAlert className="size-3.5 text-amber-500" aria-hidden="true" />
                {resumen.paused} en pausa
              </span>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
